import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, MapPin, Award, X } from 'lucide-react';
import { User } from '../types';
import { getMockUsers } from '../data/mockData';

const Athletes = () => {
  const [athletes, setAthletes] = useState<User[]>([]);
  const [filteredAthletes, setFilteredAthletes] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    sport: '',
    location: '',
    verified: false
  });

  useEffect(() => {
    document.title = 'Connect with Athletes - FemSportNet';
    
    const fetchAthletes = async () => {
      try {
        setIsLoading(true);
        const users = await getMockUsers();
        const athleteUsers = users.filter(user => user.role === 'athlete');
        setAthletes(athleteUsers);
        setFilteredAthletes(athleteUsers);
      } catch (error) {
        console.error('Error fetching athletes:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAthletes();
  }, []);

  useEffect(() => {
    let result = [...athletes];
    
    if (searchTerm) {
      result = result.filter(athlete => 
        athlete.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (athlete.sport && athlete.sport.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (athlete.location && athlete.location.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    if (filters.sport) {
      result = result.filter(athlete => 
        athlete.sport && athlete.sport.toLowerCase().includes(filters.sport.toLowerCase())
      );
    }
    
    if (filters.location) {
      result = result.filter(athlete => 
        athlete.location && athlete.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }
    
    if (filters.verified) {
      result = result.filter(athlete => athlete.verified);
    }
    
    setFilteredAthletes(result);
  }, [searchTerm, filters, athletes]);

  const clearFilters = () => {
    setFilters({
      sport: '',
      location: '',
      verified: false
    });
    setSearchTerm('');
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen py-8 transition-colors duration-200">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Connect with Elite Athletes
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Discover and connect with talented athletes from various sports around the world.
              Build your network and create meaningful professional relationships.
            </p>
          </div>

          {/* Search and Filters */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Search by name, sport, or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
              </div>
              
              <div className="flex gap-4">
                <input
                  type="text"
                  placeholder="Filter by sport"
                  value={filters.sport}
                  onChange={(e) => setFilters({ ...filters, sport: e.target.value })}
                  className="w-full md:w-40 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                
                <input
                  type="text"
                  placeholder="Filter by location"
                  value={filters.location}
                  onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                  className="w-full md:w-40 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
                
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.verified}
                    onChange={(e) => setFilters({ ...filters, verified: e.target.checked })}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="ml-2 text-gray-700 dark:text-gray-300">Verified only</span>
                </label>
              </div>
            </div>

            {/* Active Filters */}
            {(filters.sport || filters.location || filters.verified || searchTerm) && (
              <div className="mt-4 flex flex-wrap gap-2">
                {searchTerm && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                    Search: {searchTerm}
                    <button
                      onClick={() => setSearchTerm('')}
                      className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}
                
                {filters.sport && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                    Sport: {filters.sport}
                    <button
                      onClick={() => setFilters({ ...filters, sport: '' })}
                      className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}
                
                {filters.location && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                    Location: {filters.location}
                    <button
                      onClick={() => setFilters({ ...filters, location: '' })}
                      className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}
                
                {filters.verified && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                    Verified only
                    <button
                      onClick={() => setFilters({ ...filters, verified: false })}
                      className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}
                
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>

          {/* Athletes Grid */}
          {isLoading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <p className="text-gray-600 dark:text-gray-300">
                  {filteredAthletes.length} athletes found
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAthletes.map((athlete) => (
                  <Link
                    key={athlete.id}
                    to={`/profile/${athlete.id}`}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden transform transition-all duration-200 hover:-translate-y-1 hover:shadow-xl"
                  >
                    <div className="h-48 overflow-hidden relative">
                      <img
                        src={athlete.avatar}
                        alt={athlete.name}
                        className="w-full h-full object-cover"
                      />
                      {athlete.verified && (
                        <div className="absolute top-4 right-4 bg-blue-500 rounded-full p-1">
                          <Award className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </div>
                    
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                        {athlete.name}
                      </h3>
                      
                      <div className="flex items-center text-gray-600 dark:text-gray-300 mb-4">
                        <MapPin className="w-4 h-4 mr-1" />
                        <span>{athlete.location || 'Location not specified'}</span>
                      </div>
                      
                      {athlete.sport && (
                        <div className="mb-4">
                          <span className="inline-block bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-3 py-1 rounded-full text-sm font-medium">
                            {athlete.sport}
                          </span>
                        </div>
                      )}
                      
                      {athlete.bio && (
                        <p className="text-gray-600 dark:text-gray-300 text-sm mb-4 line-clamp-2">
                          {athlete.bio}
                        </p>
                      )}
                      
                      <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
                        <span>{athlete.stats.followers} followers</span>
                        {athlete.stats.matches && (
                          <span>{athlete.stats.matches} matches</span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {filteredAthletes.length === 0 && (
                <div className="text-center py-12">
                  <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
                    No athletes found
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Try adjusting your search filters to find more athletes
                  </p>
                  <button
                    onClick={clearFilters}
                    className="text-green-500 hover:text-green-600 font-medium"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Athletes;